import type { CounterSplash, ProductOfferModel } from 'autogen/swagger/all';
import { SeeOffer, TimeLeft } from 'autogen/translation-keys/trans-website-productoffer';
import { Typography } from 'components/generic/typography';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { format, formatReact } from 'modules/helpers/strings';
import Countdown from 'components/countdown/Countdown';
import type { ICountdown } from 'components/countdown/shared/types';
import { DayPlural, DaySingular } from 'autogen/translation-keys/trans-website-timedefinition';
import type { IBody } from 'components/generic/typography/components/Body';
import { GA4Tracking } from 'modules/tracking/GA4';
import InlineLinkActionButton from 'components/generic/InlineButton/InlineLinkActionButton';

const StyledBody = styled(Typography.Body)`
    margin-bottom: 0;
`;

const StyledCountdown = styled(Countdown)`
    width: auto;
`;

function getDaysDifference(endDateString: string, startDateString?: string) {
    const startDate = startDateString ? new Date(startDateString) : new Date();
    const endDate = new Date(endDateString);
    const timeDiff = endDate.getTime() - startDate.getTime();
    const daysDiff = timeDiff / (1000 * 60 * 60 * 24);

    return daysDiff;
}

function CampaignOfferCountdown({ expiryDate }: { expiryDate: CounterSplash['expiryDate'] }): JSX.Element {
    const { t } = useTranslation();

    const mapData: ICountdown = {
        showTimeUnits: true,
        sizeType: 'XS',
        displayType: 'Days',
        finishDateTime: expiryDate
    };

    return formatReact(t(TimeLeft))(<StyledCountdown countdown={mapData} />);
}

interface ICampaignOffer extends IBody {
    campaignOffer: ProductOfferModel;
}

export default function CampaignOffer({ campaignOffer, ...restProps }: ICampaignOffer): JSX.Element | null {
    const { counterSplash, offerLinkAction } = campaignOffer;
    const { expiryDate } = counterSplash || {};
    const { t } = useTranslation();

    if (!expiryDate) return null;
    const daysAmount = Math.ceil(getDaysDifference(expiryDate));

    function tracking() {
        GA4Tracking({
            eventName: 'link_click',
            category: 'navigation',
            context: 'product_page',
            eventParams: [{ param_name: 'link_text', param_value: t(SeeOffer) }]
        });
    }

    return (
        <StyledBody {...restProps}>
            {daysAmount > 1 ? (
                <span
                    dangerouslySetInnerHTML={{
                        __html: format(t(TimeLeft))(`${daysAmount} ${daysAmount === 1 ? t(DaySingular) : t(DayPlural)}`)
                    }}
                />
            ) : (
                <CampaignOfferCountdown expiryDate={expiryDate} />
            )}
            {offerLinkAction && (
                <>
                    {' - '}
                    <InlineLinkActionButton action={offerLinkAction} fontWeight="semibold" onClick={tracking}>
                        {t(SeeOffer)}
                    </InlineLinkActionButton>
                </>
            )}
        </StyledBody>
    );
}
